import * as styles from "./MentionsSection.css";

import { useAtomValue } from "jotai";
import type { JSX } from "react";

import {
  TextEditor,
  htmlToPlainText,
  plainTextToHtml,
} from "@frontend/components/organisms/TextEditor";
import { useField } from "@frontend/hooks/form";
import type { TextField } from "@frontend/lib/form";
import { useLingui } from "@lingui/react/macro";

import { projectEditorAtom, projectForm } from "../stores/editor";

const fields = projectForm.fields;

/**
 * What the bill says: its wording, and the notes that go with it. Each
 * editor names itself in its toolbar, so the pair needs no legend above.
 */
export function MentionsSection(): JSX.Element {
  const { t } = useLingui();

  const { project } = useAtomValue(projectEditorAtom);

  // The editors are seeded once per project: they are not fed back what
  // they emit, since the text they emit is not the HTML they hold.
  const key = project?.id ?? "new";

  return (
    <div className={styles.grid}>
      <MentionField
        key={`wording-${key}`}
        label={t`Bill wording`}
        field={fields.billingWording}
      />

      <MentionField
        key={`comments-${key}`}
        label={t`Billing notes`}
        field={fields.billingComments}
      />
    </div>
  );
}

/**
 * A rich editor over a plain text field: the bill only prints text, so the
 * paragraphs and lists come back as lines.
 */
function MentionField({
  label,
  field,
}: {
  label: string;
  field: TextField<string | null | undefined>;
}): JSX.Element {
  const { value, setValue } = useField(field);

  return (
    <TextEditor
      fill
      size="sm"
      rows={4}
      label={label}
      toolbarVariant="ghost"
      defaultValue={plainTextToHtml(value ?? "")}
      onValueChange={(html) => setValue(htmlToPlainText(html))}
    />
  );
}
